/**
 * LocalLifeTabs — dashboard-only compact reference panel that merges the
 * four separate always-open sections (Legal & Identity, Home & Essentials,
 * Money & Digital, Mobility) into one tabbed "Local Life" component.
 *
 * This is REFERENCE material only. Actionable "do this by date X" content
 * lives in the task board above (Before You Fly / First 12 Days / Settling In)
 * so nothing here duplicates a task — this panel answers WHERE / HOW / WHICH.
 *
 * Data source: same `/api/post-arrival/{country}` payload that
 * `CountryPostArrival` uses on the public dossier.  We keep the full-detail
 * variant of Legal & Identity here (with the "why", gates, steps, city
 * offices, national portal, gotchas) because those are genuinely reference
 * content, not task-board actions.
 */
import { useState } from "react";
import {
  ExternalLink, MapPin, ChevronRight, Landmark, AlertTriangle,
  Home as HomeIcon, Wallet, Train, Landmark as LandmarkIcon,
} from "lucide-react";

const TABS = [
  { key: "registration", label: "Registration",   icon: LandmarkIcon },
  { key: "shopping",     label: "Shopping & SIM", icon: HomeIcon },
  { key: "banking",      label: "Banking",        icon: Wallet },
  { key: "transport",    label: "Transport",      icon: Train },
];

export default function LocalLifeTabs({ pa, hostCity, matchedOffice }) {
  const [active, setActive] = useState("registration");
  if (!pa) return null;

  const homeCat = pa.categories?.find(c => c.key === "home");
  const moneyCat = pa.categories?.find(c => c.key === "money");
  const mobilityCat = pa.categories?.find(c => c.key === "mobility");
  const regCat = pa.categories?.find(c => c.key === "registration");

  return (
    <div
      className="mt-8 moe-card p-6 md:p-8 moe-fade-up"
      data-testid="section-local-life"
    >
      <div className="text-xs uppercase tracking-widest text-elec font-bold">Local life</div>
      <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
        Where to look, once you land.
      </h2>
      <p className="mt-1 text-sm text-muted max-w-2xl">
        Reference material. Task deadlines live on the board above.
      </p>

      {/* Tab strip */}
      <div className="mt-6 flex flex-wrap gap-1.5 border-b border-ink/10 pb-0" role="tablist" data-testid="local-life-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={isActive}
              data-testid={`local-life-tab-${t.key}`}
              onClick={() => setActive(t.key)}
              className={
                "inline-flex items-center gap-2 px-3.5 py-2.5 text-sm font-bold rounded-t-lg transition-colors " +
                (isActive
                  ? "text-ink bg-tint border-b-2 border-ink"
                  : "text-muted hover:text-ink hover:bg-tint/60 border-b-2 border-transparent")
              }
            >
              <Icon size={14} strokeWidth={2} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="mt-6" data-testid={`local-life-content-${active}`}>
        {active === "registration" && (
          <RegistrationTab pa={pa} hostCity={hostCity} matchedOffice={matchedOffice} regCat={regCat} />
        )}
        {active === "shopping" && (
          <ShoppingTab homeCat={homeCat} />
        )}
        {active === "banking" && (
          <BankingTab moneyCat={moneyCat} />
        )}
        {active === "transport" && (
          <TransportTab mobilityCat={mobilityCat} />
        )}
      </div>
    </div>
  );
}

// ------------------- REGISTRATION -------------------
function RegistrationTab({ pa, hostCity, matchedOffice, regCat }) {
  return (
    <div className="space-y-5">
      {/* Priority-action context (why + when). Reference, not action */}
      {pa.priority_action && (
        <div className="rounded-xl bg-tint p-4 md:p-5">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">
              {pa.priority_action.title}
            </div>
            {pa.priority_action.deadline && (
              <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded bg-ink text-white">
                {pa.priority_action.deadline}
              </span>
            )}
          </div>
          {pa.priority_action.why && (
            <p className="mt-2 text-sm text-ink leading-snug">{pa.priority_action.why}</p>
          )}
          {pa.priority_action.gates?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-soft">Blocked until done</div>
              <ul className="mt-1.5 flex flex-wrap gap-1.5">
                {pa.priority_action.gates.map((g, i) => (
                  <li key={i} className="text-[12px] font-semibold px-2 py-1 border border-ink/25 rounded-full">
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {pa.priority_action.steps?.length > 0 && (
            <ol className="mt-3 space-y-1.5">
              {pa.priority_action.steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[13px] text-ink">
                  <span className="w-5 h-5 rounded-full bg-ink text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Personalised city callout (dashboard) */}
      {hostCity && matchedOffice && (
        <div className="border-2 border-elec rounded-xl p-4 bg-elec/5" data-testid="dash-matched-office">
          <div className="text-[10px] uppercase tracking-[0.2em] text-elec font-bold">Your host city · {matchedOffice.city}</div>
          <a href={matchedOffice.url} target="_blank" rel="noopener" className="mt-2 inline-flex items-center gap-2 font-bold text-ink hover:text-elec">
            Open {matchedOffice.city} registration portal
            <ExternalLink size={14} />
          </a>
          {matchedOffice.note && <div className="mt-1 text-xs text-muted">{matchedOffice.note}</div>}
        </div>
      )}
      {hostCity && !matchedOffice && pa.city_offices?.length > 0 && (
        <div className="border border-ink/15 rounded-xl p-4 bg-tint" data-testid="dash-fallback-office">
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Your host city · {hostCity}</div>
          <div className="mt-1 text-sm text-ink">
            We don't have a direct link for {hostCity} yet. Use this formula:
          </div>
          <div className="mt-2 text-sm font-semibold text-ink">
            {(pa.city_fallback_formula || "").replace("[city]", hostCity)}
          </div>
        </div>
      )}

      {/* National portal (France ANEF style) */}
      {pa.national_portal?.url && (
        <a
          href={pa.national_portal.url}
          target="_blank" rel="noopener"
          className="block border border-ink/15 rounded-lg p-4 hover:bg-tint transition-colors"
          data-testid="national-portal"
        >
          <div className="flex items-start gap-3">
            <Landmark size={18} className="text-elec mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold text-ink">{pa.national_portal.name}</div>
              {pa.national_portal.note && <div className="mt-1 text-xs text-muted leading-snug">{pa.national_portal.note}</div>}
              <div className="mt-1 text-[11px] text-elec truncate">
                {pa.national_portal.url.replace(/^https?:\/\//, "")}
              </div>
            </div>
            <ExternalLink size={12} className="text-ink/50 shrink-0" />
          </div>
        </a>
      )}

      {/* City-office grid (for city-based countries) */}
      {pa.city_offices?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Other cities</div>
          <div className="mt-2 grid sm:grid-cols-2 gap-2" data-testid="city-office-list">
            {pa.city_offices.map((c) => {
              const isYours = hostCity && c.city.toLowerCase() === hostCity.toLowerCase();
              return (
                <a
                  key={c.city}
                  href={c.url}
                  target="_blank" rel="noopener"
                  data-testid={`office-${c.city.toLowerCase()}`}
                  className={
                    "flex items-start gap-3 border rounded-lg px-3 py-2.5 hover:bg-tint transition-colors group " +
                    (isYours ? "border-elec bg-elec/5" : "border-ink/15")
                  }
                >
                  <MapPin size={13} className="mt-0.5 text-elec shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-ink text-sm">
                      {c.city}
                      {isYours && <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-elec text-white font-bold uppercase tracking-widest">Yours</span>}
                    </div>
                    {c.note && <div className="text-[11px] text-muted leading-snug">{c.note}</div>}
                  </div>
                  <ChevronRight size={13} className="text-ink/40 group-hover:text-elec shrink-0" />
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Registration gotchas */}
      {regCat?.gotchas?.length > 0 && (
        <div className="space-y-2.5" data-testid="registration-gotchas">
          {regCat.gotchas.map((g, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-[color:var(--moe-yolk)]/25 border border-[color:var(--moe-yolk)]" data-testid={`registration-gotcha-${i}`}>
              <AlertTriangle size={15} className="text-ink shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-ink text-sm">{g.title}</div>
                {g.detail && <div className="mt-0.5 text-[12.5px] text-ink leading-snug">{g.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------- SHOPPING & SIM -------------------
function ShoppingTab({ homeCat }) {
  if (!homeCat?.groups?.length) {
    return <div className="text-sm text-muted">Shopping and SIM data not yet published for this country.</div>;
  }
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
        {homeCat.groups.map(g => (
          <div key={g.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`home-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {g.items.map(i => (
                <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {homeCat.free_alternatives?.length > 0 && (
        <div className="pt-4 border-t border-ink/10" data-testid="home-free-alternatives">
          {homeCat.free_alternatives.map(block => (
            <div key={block.label} className="mt-3 first:mt-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">Free / non-monetised · {block.label}</div>
              <div className="mt-2 grid sm:grid-cols-3 gap-2">
                {block.items.map(i => (
                  <a
                    key={i.name}
                    href={i.url}
                    target="_blank" rel="noopener"
                    className="flex items-center justify-between gap-2 border border-dashed border-ink/25 rounded-lg px-3 py-2 hover:bg-tint transition-colors group"
                    data-testid={`free-alt-${i.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <div className="min-w-0">
                      <div className="font-bold text-ink text-sm truncate">{i.name}</div>
                      {i.note && <div className="text-[11px] text-muted truncate">{i.note}</div>}
                    </div>
                    <ExternalLink size={11} className="text-ink/50 group-hover:text-elec shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------- BANKING -------------------
function BankingTab({ moneyCat }) {
  if (!moneyCat?.groups?.length) {
    return <div className="text-sm text-muted">Banking data not yet published for this country.</div>;
  }
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
        {moneyCat.groups.map(g => (
          <div key={g.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`money-${g.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              {g.items.map(i => (
                <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="text-[11.5px] text-muted italic">
        Blocked account, forex and insurance partners → see the Nikshala partner services block below.
      </div>
    </div>
  );
}

// ------------------- TRANSPORT -------------------
function TransportTab({ mobilityCat }) {
  if (!mobilityCat?.groups?.length) {
    return <div className="text-sm text-muted">Transport data not yet published for this country.</div>;
  }
  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-x-6 gap-y-5">
        {mobilityCat.groups.map(g => (
          <div key={g.label}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-soft font-bold">{g.label}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {g.items.map(i => (
                <span key={i} className="px-2.5 py-1 text-[12px] font-semibold border border-ink/25 rounded-full">{i}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
      {mobilityCat.gotchas?.length > 0 && (
        <div className="space-y-2.5" data-testid="mobility-gotchas">
          {mobilityCat.gotchas.map((g, i) => (
            <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-[color:var(--moe-yolk)]/25 border border-[color:var(--moe-yolk)]" data-testid={`gotcha-${i}`}>
              <AlertTriangle size={15} className="text-ink shrink-0 mt-0.5" />
              <div>
                <div className="font-bold text-ink text-sm">{g.title}</div>
                {g.detail && <div className="mt-0.5 text-[12.5px] text-ink leading-snug">{g.detail}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
